/**
 * transformAdapters — the bridge between the transform gizmo and the very
 * different ways this app stores an object's placement.
 *
 * The gizmo speaks one language: position / rotation / scale as plain arrays.
 * The objects don't. The environment splat is a real TRS in
 * liveTuneStore.environment.pose; the avatar is a live THREE.Group whose XZ is
 * also driven by the movement component; the spotlight (slice 3) is
 * distance/height/yaw on an orbit rig; the camera cage is half-extents with no
 * position of its own.
 *
 * So each selectable object gets an adapter:
 *
 *   id           stable key, also what sceneSelection stores
 *   label        outliner row text
 *   caps         which gizmo modes/axes make sense — see CAPS below
 *   note         optional one-liner shown in the outliner (e.g. "not saved")
 *   isAvailable  false → the object isn't in the scene right now
 *   read()       -> { position:[x,y,z], rotation:[x,y,z], scale:[x,y,z] }
 *   write(trs)   apply a gizmo edit back to wherever it actually lives
 *
 * read/write are the ONLY things that know an object's storage. Adding the
 * spotlight later means adding an adapter, not touching the gizmo.
 *
 * Rotation is radians throughout (matches what Scene.jsx feeds three).
 *
 * The factories take their dependencies so unit tests can drive them without a
 * browser; `getAdapters()` wires the real singletons.
 */

import { liveTuneStore } from './liveTuneStore.js'
import { movement } from './movementComponent.js'
import { normalizePose, posesEqual } from './envPose.js'
import { sphericalToXYZ, xyzToSpherical } from './lightPlacement.js'
import { avatarFloor } from './avatarFloor.js'

/**
 * caps values, per mode:
 *   translate: 'xyz' | 'xz'  | false
 *   rotate:    'xyz' | 'y'   | false
 *   scale:     'xyz' | 'uniform' | false
 * A `false` mode is not offered in the outliner and the gizmo refuses it.
 */
export const AXES_BY_CAP = {
  xyz: { x: true, y: true, z: true },
  xz: { x: true, y: false, z: true },
  y: { x: false, y: true, z: false },
  // 'uniform' shows every handle and enforces uniformity in the WRITE instead
  // of by hiding handles. That is not a style choice — three's TransformControls
  // gates visibility with a SUBSTRING test:
  //
  //     handle.visible &&= ( handle.name.indexOf('X') === -1 || this.showX )   // …and Y, Z
  //
  // The centre uniform-scale handle is named 'XYZ', so it contains X, Y and Z.
  // Setting showX/showY/showZ all false to "leave only the centre box" hides
  // the centre box as well, and the gizmo renders nothing at all. There is no
  // supported way to isolate it (PlayCanvas has per-shape enable; three does
  // not), so every handle stays grabbable and collapseUniformScale() below
  // turns whichever one you drag into a single factor.
  uniform: { x: true, y: true, z: true },
}

/**
 * Collapse a possibly non-uniform scale triple to the single factor the store
 * can actually hold (default_pose.scale is one scalar).
 *
 * Picks the component furthest from `base`, which MUST be the scale at the
 * START of the drag — not the current stored value.
 *
 * Why that distinction matters: three recomputes the whole scale from a
 * drag-start snapshot on every move (`object.scale.copy(_scaleStart).multiply(…)`),
 * so the two axes you are NOT dragging stay pinned at the drag-start value for
 * the entire drag. If `base` tracked the live stored value instead, then as the
 * dragged axis pulls the store away, those two pinned axes would end up further
 * from `base` than the dragged one — and the pick would flip to them, snapping
 * the object back to where the drag started. A fixed base makes exactly one
 * component deviate, stably, for the whole gesture.
 */
export function collapseUniformScale(scale, base) {
  if (!Array.isArray(scale) || scale.length === 0) return base
  let best = base
  let bestDelta = -1
  for (const v of scale) {
    if (!Number.isFinite(v)) continue
    const d = Math.abs(v - base)
    if (d > bestDelta) { bestDelta = d; best = v }
  }
  return best
}

/** Which show{X,Y,Z} the gizmo should use for a given adapter + mode. */
export function axesFor(caps, mode) {
  const cap = caps?.[mode]
  if (!cap) return null
  return AXES_BY_CAP[cap] ?? AXES_BY_CAP.xyz
}

export function modeAllowed(caps, mode) {
  return Boolean(caps?.[mode])
}

// Guard against the scale-0 trap normalizePose already defends: a 0 scale
// renders the splat invisible with no error, which reads as "the environment
// broke". The gizmo can produce a 0 by dragging the centre box through the
// origin, so clamp here too.
const MIN_SCALE = 1e-4

// ──────────────────────────────────────────────────────────────────────
// Environment splat
// ──────────────────────────────────────────────────────────────────────

/**
 * @param store      liveTuneStore (or a fake, in tests)
 * @param getEnvRow  () => the server asset row for the ACTIVE environment, or
 *                   null. Supplies the saved default_pose that `dirty` compares
 *                   against, and the id that `save` PATCHes.
 * @param saveEnvPose (id, pose) => Promise. Persists to the server.
 */
export function createEnvironmentAdapter({ store, getEnvRow, saveEnvPose }) {
  return {
    id: 'environment',
    label: 'Environment',
    // Uniform only: a non-uniform splat scale has no sensible authoring
    // meaning here (default_pose stores a single `scale` scalar), and
    // supersplat makes the same call.
    caps: { translate: 'xyz', rotate: 'xyz', scale: 'uniform' },

    isAvailable: () => Boolean(store.getState().environment.splatUrl),

    /**
     * 'dirty' means the live pose differs from what the server has, i.e. this
     * edit exists only in this browser's localStorage and no teammate will see
     * it. Returns 'clean' when there is no server row to compare against —
     * a URL-param splat has nowhere to be saved, so claiming "unsaved" would be
     * telling the user to press a button that cannot help them.
     */
    dirty() {
      const row = getEnvRow?.()
      if (!row) return 'clean'
      const saved = row.defaultPose ?? row.default_pose ?? {}
      return posesEqual(saved, store.getState().environment.pose) ? 'clean' : 'dirty'
    },

    /** Present only when there is somewhere to save to — the UI hides the button otherwise. */
    canSave: () => Boolean(getEnvRow?.()?.id),

    async save() {
      const row = getEnvRow?.()
      if (!row?.id) throw new Error('no environment selected')
      await saveEnvPose(row.id, normalizePose(store.getState().environment.pose))
    },

    read() {
      const p = store.getState().environment.pose
      const s = Number.isFinite(p.scale) && p.scale > 0 ? p.scale : 1
      return {
        position: [p.px, p.py, p.pz],
        rotation: [p.rx, p.ry, p.rz],
        scale: [s, s, s],
      }
    },

    // `dragStart` is the transform read at the moment the drag began, supplied
    // by TransformGizmo. Required for a correct uniform collapse — see
    // collapseUniformScale. Absent (a programmatic write), the live value is a
    // fine stand-in because there is no in-flight gesture to be unstable across.
    write({ position, rotation, scale }, dragStart) {
      const pose = {}
      if (position) { [pose.px, pose.py, pose.pz] = position }
      if (rotation) { [pose.rx, pose.ry, pose.rz] = rotation }
      if (scale) {
        const startScale = dragStart?.scale?.[0]
        const cur = store.getState().environment.pose.scale
        const base = Number.isFinite(startScale) && startScale > 0
          ? startScale
          : (Number.isFinite(cur) && cur > 0 ? cur : 1)
        pose.scale = Math.max(MIN_SCALE, collapseUniformScale(scale, base))
      }
      store.setState({ environment: { pose } })
    },
  }
}

// ──────────────────────────────────────────────────────────────────────
// Avatar
// ──────────────────────────────────────────────────────────────────────

export function createAvatarAdapter({ getGroup, getMovement }) {
  return {
    id: 'avatar',
    label: 'Avatar',
    // Full XYZ translate + XYZ rotate + uniform scale — the PlayCanvas/Unreal
    // model, where a selected object accepts every axis unless something else
    // genuinely owns it.
    //   - Y position WAS refused because avatarFloor re-asserts
    //     group.position.y on every floor change, so a direct write vanished.
    //     The fix is composition, not refusal: the gizmo writes
    //     avatarFloor.setLift and the floor snap adds it, so both writers agree
    //     and a vertical drag survives an environment swap.
    //   - X/Z rotation WAS refused because movementComponent steers
    //     group.rotation.y while pathing. That conflict is already handled by
    //     the `moving` guard in write(), which refuses the whole transform —
    //     capping the axes on top of it just removed pitch and roll from a
    //     stationary character for no reason.
    //   - scale is uniform because a sheared rig is never what anyone wants.
    caps: { translate: 'xyz', rotate: 'xyz', scale: 'uniform' },
    note: 'Runtime only — not saved',

    isAvailable: () => Boolean(getGroup()),

    // Nothing to persist: the avatar's placement is transient runtime state,
    // which the row's `note` says out loud. Always-clean is the honest answer —
    // a dirty badge would imply a save that does not exist.
    dirty: () => 'clean',
    canSave: () => false,

    read() {
      const g = getGroup()
      if (!g) return { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] }
      return {
        position: [g.position.x, g.position.y, g.position.z],
        rotation: [g.rotation.x, g.rotation.y, g.rotation.z],
        // The REAL group scale, not a hardcoded 1: the inspector's Scale fields
        // and the gizmo both read this, so reporting a constant would show 1
        // while the avatar rendered at 2.
        scale: [g.scale.x, g.scale.y, g.scale.z],
      }
    },

    write({ position, rotation, scale }, dragStart) {
      const g = getGroup()
      if (!g) return
      // The movement component drives group.position.x/z every tick while
      // pathing. Writing underneath it would be silently undone next frame, so
      // refuse instead of pretending the edit landed.
      const moving = getMovement?.()?.status === 'moving'
      if (moving) return
      if (position) {
        g.position.x = position[0]
        g.position.z = position[2]
        // Y goes through the floor module, which then writes the group: floor
        // baseline + lift. Sending the DELTA (rather than an absolute lift)
        // means the caller never needs to know the rig's bbox offset, and the
        // result survives the next floor change.
        const dy = position[1] - g.position.y
        if (dy) avatarFloor.setLift(avatarFloor.getLift() + dy)
      }
      if (rotation) g.rotation.set(rotation[0], rotation[1], rotation[2])
      if (scale) {
        // Same collapse the environment adapter uses: whichever axis the pointer
        // pulled furthest from the drag-start baseline becomes the one factor.
        const startScale = dragStart?.scale?.[0]
        const base = Number.isFinite(startScale) && startScale > 0 ? startScale : (g.scale.x || 1)
        g.scale.setScalar(Math.max(MIN_SCALE, collapseUniformScale(scale, base)))
      }
    },
  }
}

// ──────────────────────────────────────────────────────────────────────
// Splat (OGS) avatar
// ──────────────────────────────────────────────────────────────────────

/**
 * A splat character has no rig group — OGSModel mounts SplatMeshes, not a
 * skinned GLB — so the GLB avatar adapter's target (__AVATAR_GROUP__) simply
 * doesn't exist and its row stayed unselectable. This drives the dedicated
 * `ogs-avatar-root` Group that OGSModel now parents every splat under.
 *
 * Caps match the GLB avatar: full XYZ translate + rotate, uniform scale. There
 * is no avatarFloor writer here, so Y is written straight onto the root; scale
 * stays uniform because a non-uniform splat scale would shear the gaussians.
 *
 * It carries the GLB adapter's `moving` guard because movement drives this root too:
 * a drag mid-path would be silently undone on the next tick.
 *
 * Only one of this and the GLB adapter is ever available — a character resolves
 * to exactly one render path.
 */
export function createSplatAvatarAdapter({ getRoot, getMovement }) {
  return {
    id: 'splatAvatar',
    label: 'Avatar',
    caps: { translate: 'xyz', rotate: 'xyz', scale: 'uniform' },
    note: 'Runtime only — not saved',

    isAvailable: () => Boolean(getRoot()),

    // No per-character pose field exists for splat avatars yet, so there is
    // nowhere to persist this. Always-clean is the honest answer; the row's
    // note says so.
    dirty: () => 'clean',
    canSave: () => false,

    read() {
      const g = getRoot()
      if (!g) return { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] }
      return {
        position: [g.position.x, g.position.y, g.position.z],
        rotation: [g.rotation.x, g.rotation.y, g.rotation.z],
        scale: [g.scale.x, g.scale.y, g.scale.z],
      }
    },

    write({ position, rotation, scale }, dragStart) {
      const g = getRoot()
      if (!g) return
      // Refuse rather than pretend the edit landed — movement owns x/z and yaw
      // while pathing.
      if (getMovement?.()?.status === 'moving') return
      if (position) g.position.set(position[0], position[1], position[2])
      if (rotation) g.rotation.set(rotation[0], rotation[1], rotation[2])
      if (scale) {
        const startScale = dragStart?.scale?.[0]
        const base = Number.isFinite(startScale) && startScale > 0
          ? startScale
          : (g.scale.x > 0 ? g.scale.x : 1)
        const s = Math.max(MIN_SCALE, collapseUniformScale(scale, base))
        g.scale.setScalar(s)
      }
      // The appearance decoder reads this matrix (via localFrameCamera) on the
      // next inference tick, so it has to be current before then rather than
      // waiting for R3F's own traversal.
      g.updateMatrixWorld(true)
    },
  }
}

// ──────────────────────────────────────────────────────────────────────
// Registry
// ──────────────────────────────────────────────────────────────────────
// Torch
// ──────────────────────────────────────────────────────────────────────

/**
 * The movable point light (TorchLight.jsx). Dragging it is the whole point of
 * the thing — you walk it past the subject and watch the highlight follow — so
 * it gets the standard gizmo rather than one of its own.
 *
 * Storage is spherical (distance/elevation/azimuth about [0, targetHeight, 0]),
 * the same convention as every other light in the scene, so the adapter is
 * where cartesian meets spherical: read places, write un-places. That keeps ONE
 * representation in the store, and a drag lands in the very sliders the panel
 * shows instead of a competing cartesian copy.
 *
 * Rotate and scale are false because a point light has neither.
 */
export function createTorchAdapter({ store, place, unplace }) {
  const aim = () => [0, store.getState().torch?.targetHeight ?? 0, 0]
  return {
    id: 'torch',
    label: 'Torch',
    caps: { translate: 'xyz', rotate: false, scale: false },
    note: 'Placement = the TORCH sliders',

    // Ungrabbable in headlamp mode: TorchLight re-writes the position from the
    // camera every frame, so a drag would be silently undone. Refusing the row
    // beats pretending the edit landed (same call as the avatar adapter's
    // `moving` guard).
    isAvailable: () => {
      const t = store.getState().torch
      return Boolean(t?.enabled && !t.followCamera)
    },

    // The placement IS the store slice, and `torch` is in envTune.TUNE_SLICES —
    // so it persists through the panel's "Save tune for everyone", not through a
    // per-object save button here. Claiming otherwise would point the user at a
    // button that doesn't exist.
    dirty: () => 'clean',
    canSave: () => false,

    read() {
      const t = store.getState().torch
      return { position: place(t.distance, t.elevation, t.azimuth, aim()), rotation: [0, 0, 0], scale: [1, 1, 1] }
    },

    // Returns the knobs it stored, or null when it refused — the gizmo ignores
    // this, but it's what makes __TORCH__.setPos able to say "that didn't land"
    // instead of reporting a placement the headlamp overwrites next frame.
    write({ position }) {
      const t = store.getState().torch
      if (!position || !t?.enabled || t.followCamera) return null
      const knobs = unplace(position, aim())
      store.setState({ torch: knobs })
      return knobs
    },
  }
}

// ──────────────────────────────────────────────────────────────────────

let _adapters = null

// Resolve the server row for whatever environment is currently active.
// Reads CustomAssetManager's in-memory list rather than fetching — the list is
// already loaded and kept fresh by CAM, and a second fetch here would be a
// second source of truth for the same rows. Imported lazily to keep this module
// node-testable (CAM pulls in browser-only dependencies).
function activeEnvRow() {
  if (typeof window === 'undefined') return null
  const rows = window.__ENVIRONMENTS_LIST__
  if (!Array.isArray(rows)) return null
  const url = liveTuneStore.getState().environment.splatUrl
  if (!url) return null
  return rows.find(r => (r.fileUrl ?? r.file_url) === url) ?? null
}

/** All adapters, real singletons wired in. Order = outliner order. */
export function getAdapters() {
  if (!_adapters) {
    _adapters = [
      createEnvironmentAdapter({
        store: liveTuneStore,
        getEnvRow: activeEnvRow,
        saveEnvPose: async (id, pose) => {
          const mod = await import('./CustomAssetManager.js')
          await mod.CustomAssetManager.updateEnvironmentAsset(id, { defaultPose: pose })
        },
      }),
      createAvatarAdapter({
        getGroup: () => (typeof window !== 'undefined' && window.__AVATAR_GROUP__) || null,
        getMovement: () => movement,
      }),
      // Splat characters take this path instead; exactly one of the two is
      // available for any given character.
      createSplatAvatarAdapter({
        getRoot: () => (typeof window !== 'undefined' && window.__OGS_AVATAR_ROOT__) || null,
        getMovement: () => movement,
      }),
      createTorchAdapter({ store: liveTuneStore, place: sphericalToXYZ, unplace: xyzToSpherical }),
    ]
  }
  return _adapters
}

export function getAdapter(id) {
  if (!id) return null
  return getAdapters().find(a => a.id === id) ?? null
}
